import { OperationType } from '@aavaz-ai/eql-parser-ts/dist/types';
import { useUpdateAnalysis } from '@modules/analysis/hooks/useUpdateAnalysis';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import useFeedbackListStore from '@modules/feedback/store/useFeedbackListStore';
import { isSavedSnippetIDMatching } from '@modules/feedback/utils';
import { trackCollectionEvents } from '@modules/savedCollection/utils';
import { SavedFeedbackListActionType } from '@modules/savedFeedbackList/store/savedFeedbackListActionType';
import { SavedItemsMap } from '@modules/savedFeedbackList/store/types';
import { useSavedFeedbackListStore } from '@modules/savedFeedbackList/store/useSavedFeedbackListStore';
import { useContext } from 'react';
import { FieldNames } from 'v2/core/entities/fields';
import { useGetEQLExpression } from 'v2/core/repository/edge-server/eql/useGetEQLExpression';
import useGetSourceDetailsMap from 'v2/core/repository/edge-server/source-details';
import { parseEQL } from 'v2/core/repository/eql/parser';
import { ExpressionType } from 'v2/core/repository/eql/types';
import { getWhereClauseForFieldName } from 'v2/core/services/queryBuilder/whereClauseGenerator';
import { useGetUser } from 'v2/core/services/user';
import { OpenInCollectionLink } from '../OpenInCollectionLink';
import { SaveFeedbackContext } from '../SaveFeedbackContext';

interface UseRemoveFeedbackFromCollectionProps {
  feedbackID: string;
  collectionEditToken?: string;
  collectionEQL: string;
  collectionID: string;
  collectionTitle: string;
  collectionViewToken: string;
  shouldShowOpenCollectionLink?: boolean;
  savedItemsMap?: SavedItemsMap;
}

export const useRemoveFeedbackFromCollection = ({
  feedbackID,
  collectionEditToken,
  collectionEQL,
  collectionID,
  collectionTitle,
  collectionViewToken,
  shouldShowOpenCollectionLink = true,
  savedItemsMap: savedItemsMapProp,
}: UseRemoveFeedbackFromCollectionProps) => {
  const {
    tenant: { id: tenantID, timezone },
  } = useGetUser();
  const { data } = useGetSourceDetailsMap();
  const { showSuccessNotification } = useNotificationContext();
  const contextData = useContext(SaveFeedbackContext);
  const isSnippet = contextData?.snippetID;
  const { savedItemsMap: savedItemsMapFromStore, dispatch } =
    useSavedFeedbackListStore();
  const { renderedInsideCollectionID } = useFeedbackListStore();

  const savedItemsMap = savedItemsMapProp ?? savedItemsMapFromStore;

  const getFilteredSnippet = () => {
    if (isSnippet) {
      const existingMetadataSnippets =
        savedItemsMap[feedbackID]?.snippetMetadataV2 ?? [];

      const filteredSnippet = existingMetadataSnippets.filter(
        (snippet) =>
          !(
            snippet.location.keywordSummary?.labelID === contextData.labelID &&
            snippet.location.keywordSummary?.msgIdx === contextData.summaryIndex
          ),
      );

      return filteredSnippet;
    }

    return [];
  };

  const existingFeedbackIDsOfCollection =
    (data &&
      parseEQL(collectionEQL, data, timezone).whereClause?.filter?.idFilter
        ?.ids) ??
    [];

  const updatedFeedbackIDsOfCollection = (() => {
    const hasMatchingSnippetID =
      !!contextData?.snippetID &&
      existingFeedbackIDsOfCollection.find((id) =>
        isSavedSnippetIDMatching({
          savedSnippetID: id,
          feedbackID,
          savedFeedbackContext: contextData,
        }),
      );

    if (hasMatchingSnippetID) {
      return existingFeedbackIDsOfCollection.filter(
        (id) =>
          !isSavedSnippetIDMatching({
            savedSnippetID: id,
            feedbackID,
            savedFeedbackContext: contextData,
          }),
      );
    }

    return isSnippet && getFilteredSnippet().length > 0
      ? existingFeedbackIDsOfCollection
      : existingFeedbackIDsOfCollection.filter(
          (savedFeedbackID) => savedFeedbackID !== feedbackID,
        );
  })();

  const updatedWhereClause = getWhereClauseForFieldName({
    fieldName: FieldNames.ID,
    values:
      updatedFeedbackIDsOfCollection.length > 0
        ? updatedFeedbackIDsOfCollection
        : [''],
    op: OperationType.AND,
  });

  const { updateAnalysis, isLoading } = useUpdateAnalysis({});

  const { eql } = useGetEQLExpression({
    tenantID,
    eqlProps: {
      whereClause: updatedWhereClause,
      expressionType: ExpressionType.Records,
    },
  });

  const removeFeedbackFromCollection = async () => {
    if (!collectionEditToken || !eql || !collectionEditToken) {
      return;
    }

    if (isSnippet) {
      await updateAnalysis({
        variables: {
          id: collectionID,
          tenantID: tenantID,
          editToken: collectionEditToken,
          context: {
            eqlExpression: eql,
          },
          metadataV2: {
            collection: {
              savedFeedbackMetadata: [
                {
                  feedbackID: feedbackID,
                  snippetMetadataV2: getFilteredSnippet(),
                },
              ],
            },
          },
        },
      });

      trackCollectionEvents({
        eventName: 'Remove Snippet From Collection',
        snippetId: contextData.snippetID,
        collectionId: collectionID,
        collectionName: collectionTitle,
        feedbackId: feedbackID,
      });
    } else {
      await updateAnalysis({
        variables: {
          id: collectionID,
          tenantID: tenantID,
          editToken: collectionEditToken,
          context: {
            eqlExpression: eql,
          },
          metadataV2: {
            collection: {
              savedFeedbackMetadata: [],
            },
          },
        },
      });

      trackCollectionEvents({
        eventName: 'Remove Feedback From Collection',
        collectionId: collectionID,
        collectionName: collectionTitle,
        feedbackId: feedbackID,
      });

      if (collectionID === renderedInsideCollectionID) {
        dispatch({
          type: SavedFeedbackListActionType.UPDATE_SAVED_FEEDBACK_LIST,
          payload: updatedFeedbackIDsOfCollection,
        });
      }
    }

    showSuccessNotification({
      contentComponent: `${
        isSnippet ? 'Snippet' : 'Feedback record'
      } removed from "${collectionTitle}"`,
      props: {
        severity: 'success',
        timeout: 15000,
        isClosable: true,
        action: shouldShowOpenCollectionLink ? (
          <OpenInCollectionLink viewToken={collectionViewToken ?? ''} />
        ) : null,
      },
    });
  };

  return {
    removeFeedbackFromCollection,
    isLoading,
  };
};
